import * as React from "react";
import {
  promoteAgreementVersion,
  resolveVersionItems,
  type VersionSummary,
  type VersionsResponse,
} from "../../agreement";
import { showPluginToast } from "../shell/PluginToast";
import { PromoteVersionDialog } from "./PromoteVersionDialog";
import { VersionMajorRow } from "./VersionMajorRow";
import { VersionRevisionRow } from "./VersionRevisionRow";
import { IconCompare, IconInfo } from "./VersionIcons";
import {
  countActiveRevisions,
  groupVersionsByMajor,
} from "./versionHistoryUtils";
import {
  prepareUploadFilename,
  suggestPromoteUploadFilenamePattern,
} from "./uploadFilename";
import "./versions-ui.scss";

export type VersionsPanelProps = {
  versionsData: VersionsResponse | null;
  versionsStatus: string;
  versionsError: string;
  loading: boolean;
  onRefresh?: () => void;
};

export const VersionsPanel: React.FC<VersionsPanelProps> = ({
  versionsData,
  versionsStatus,
  versionsError,
  loading,
  onRefresh,
}) => {
  const items = React.useMemo(() => resolveVersionItems(versionsData), [versionsData]);
  const grouped = React.useMemo(() => groupVersionsByMajor(items), [items]);
  const activeRevisionCount = React.useMemo(() => countActiveRevisions(items), [items]);
  const currentId = versionsData?.current_version_id?.trim() || null;
  const currentLabel = (
    versionsData?.display_label?.trim() ||
    versionsData?.current_version?.trim() ||
    ""
  );

  const isItemCurrent = React.useCallback(
    (v: { id: string; version_id?: string; display_label?: string }) => {
      if (currentId) {
        return v.id === currentId || v.version_id === currentId;
      }
      // Fallback only when API omits current_version_id — exact label match, never prefix.
      if (currentLabel) {
        const label = v.display_label?.trim();
        return Boolean(label && label === currentLabel);
      }
      return false;
    },
    [currentId, currentLabel],
  );

  const [expanded, setExpanded] = React.useState<Record<number, boolean>>({});
  const [promotingId, setPromotingId] = React.useState<string | null>(null);
  const [promoteTarget, setPromoteTarget] = React.useState<VersionSummary | null>(null);
  const [promoteFilename, setPromoteFilename] = React.useState("document.docx");

  const nextMajorVersion = React.useMemo(() => {
    if (items.length === 0) return 1;
    const max = items.reduce((m, v) => Math.max(m, Number(v.version) || 0), 0);
    return max + 1;
  }, [items]);

  React.useEffect(() => {
    if (!promoteTarget) return;
    setPromoteFilename(
      suggestPromoteUploadFilenamePattern(
        nextMajorVersion,
        promoteTarget.source_doc_filename,
      ),
    );
  }, [promoteTarget, nextMajorVersion]);

  const confirmPromote = React.useCallback(async () => {
    if (!promoteTarget) return;
    const versionId = promoteTarget.id;
    const label = promoteTarget.display_label || `v${promoteTarget.version}`;
    setPromotingId(versionId);
    try {
      const result = await promoteAgreementVersion(
        versionId,
        undefined,
        prepareUploadFilename(promoteFilename),
      );
      showPluginToast(`Promoted ${label} → ${result.version.display_label}.`);
      setPromoteTarget(null);
      onRefresh?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showPluginToast(msg || "Could not promote this revision.");
    } finally {
      setPromotingId(null);
    }
  }, [promoteTarget, promoteFilename, onRefresh]);

  React.useEffect(() => {
    if (grouped.length === 0) return;
    setExpanded((prev) => {
      const next: Record<number, boolean> = { ...prev };
      let changed = false;
      for (const { major, revisions } of grouped) {
        if (next[major.version] !== undefined) continue;
        const hasCurrentRev = revisions.some((r) => isItemCurrent(r));
        next[major.version] = revisions.length > 0 || hasCurrentRev;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [grouped, isItemCurrent]);

  const toggle = (versionNum: number) => {
    setExpanded((prev) => ({ ...prev, [versionNum]: !prev[versionNum] }));
  };

  if (loading && !versionsData) {
    return (
      <div className="versions-panel versions-panel--loading">
        <span className="versions-spinner" aria-hidden />
        <span className="versions-panel__status">{versionsStatus || "Loading versions…"}</span>
      </div>
    );
  }

  return (
    <div className="versions-panel">
      <div className="versions-header">
        <span className="versions-header__title">Version history</span>
        <button
          type="button"
          className="versions-compare-btn"
          onClick={() => showPluginToast("Demo — compare is coming soon.")}
        >
          <IconCompare />
          Compare
        </button>
      </div>

      {versionsError ? (
        <div className="versions-alert" role="alert">
          <span>{versionsError}</span>
          {onRefresh ? (
            <button type="button" className="versions-alert__retry" onClick={onRefresh}>
              Retry
            </button>
          ) : null}
        </div>
      ) : null}

      {!loading && !versionsError && !versionsData ? (
        <p className="versions-empty">No version data loaded.</p>
      ) : null}

      {activeRevisionCount > 0 ? (
        <div className="versions-banner" role="status">
          <span className="versions-banner__icon">
            <IconInfo size={13} />
          </span>
          <span>
            {activeRevisionCount} version{activeRevisionCount === 1 ? "" : "s"} ready to
            promote — use Promote to make the next major version.
          </span>
        </div>
      ) : null}

      {versionsData && grouped.length === 0 ? (
        <p className="versions-empty">No version history found</p>
      ) : null}

      {grouped.length > 0 ? (
        <div className="versions-list">
          {grouped.map((group, i) => {
            const isCurrentMajor = isItemCurrent(group.major);
            const isExpanded =
              group.revisions.length > 0 && expanded[group.major.version] === true;
            return (
              <div
                key={group.major.id}
                className={`versions-major-wrap${i < grouped.length - 1 ? " has-divider" : ""}`}
              >
                <VersionMajorRow
                  group={group}
                  expanded={isExpanded}
                  isCurrent={isCurrentMajor}
                  onToggle={() => toggle(group.major.version)}
                  promoting={promotingId === group.major.id}
                  onPromote={
                    group.major.is_promotable
                      ? () => setPromoteTarget(group.major)
                      : undefined
                  }
                >
                  {group.revisions.map((rev) => (
                    <VersionRevisionRow
                      key={rev.id}
                      version={rev}
                      isCurrent={isItemCurrent(rev)}
                      promoting={promotingId === rev.id}
                      onPromote={
                        rev.is_promotable ? () => setPromoteTarget(rev) : undefined
                      }
                    />
                  ))}
                </VersionMajorRow>
              </div>
            );
          })}
        </div>
      ) : null}

      {loading && versionsData ? (
        <div className="versions-panel__refreshing">
          <span className="versions-spinner versions-spinner--sm" aria-hidden />
          <span>{versionsStatus || "Refreshing…"}</span>
        </div>
      ) : null}

      <PromoteVersionDialog
        open={promoteTarget !== null}
        revisionLabel={promoteTarget?.display_label || ""}
        nextMajorVersion={nextMajorVersion}
        filename={promoteFilename}
        onFilenameChange={setPromoteFilename}
        pending={Boolean(promotingId)}
        onCancel={() => {
          if (!promotingId) setPromoteTarget(null);
        }}
        onConfirm={() => void confirmPromote()}
      />
    </div>
  );
};
